/**
 * Client-side notification catalog: event_type → icon, Persian label, message
 * builder and deep-link route. The SERVER decides who gets notified (triggers
 * in migration 20260718120000); this module only decides how a row RENDERS.
 *
 * Kept in lockstep with the DB catalog (`notification_event_types`) — the
 * unit suite asserts this list matches `NOTIFICATION_EVENT_TYPES`, and any
 * event type the client does not know still renders via the DB catalog's
 * label + the payload fields (see `configFor` fallback), so an unknown type
 * degrades gracefully instead of breaking the inbox.
 *
 * Mirrored (deliberately, mobile is not a workspace member) in
 * apps/mobile/src/features/notifications/lib/notification-config.ts — change
 * both or the parity unit tests fail.
 */

export interface NotificationLike {
  event_type: string
  project_id: string | null
  record_id: string | null
  payload: Record<string, unknown> | null
}

export interface NotificationTypeConfig {
  /** lucide icon name, resolved by the item component */
  icon: string
  labelFa: string
  message: (n: NotificationLike) => string
  route: (n: NotificationLike) => string | null
}

/** Every event type the server can emit — mirror of notification_event_types. */
export const NOTIFICATION_EVENT_TYPES = [
  'record_submitted_for_review',
  'record_approved',
  'record_declined',
  'record_resubmitted',
  'record_commented',
  'comment_reply',
  'comment_mention',
  'member_added',
  'member_role_changed',
  'form_major_published',
  'record_marked_for_revision',
] as const

export const ROLE_LABELS_FA: Record<string, string> = {
  admin: 'مدیر',
  data_entry: 'ثبت‌کننده داده',
  approver: 'تأییدکننده',
  viewer: 'مشاهده‌گر',
}

function p(n: NotificationLike, key: string): string {
  const v = n.payload?.[key]
  return typeof v === 'string' || typeof v === 'number' ? String(v) : ''
}

function roleFa(n: NotificationLike, key: string): string {
  return ROLE_LABELS_FA[p(n, key)] ?? p(n, key)
}

const recordRoute = (n: NotificationLike): string | null =>
  n.project_id && n.record_id
    ? `/projects/${n.project_id}/records?record=${n.record_id}`
    : n.project_id
      ? `/projects/${n.project_id}/records`
      : null

const recordsListRoute = (n: NotificationLike): string | null =>
  n.project_id ? `/projects/${n.project_id}/records` : null

const overviewRoute = (n: NotificationLike): string | null =>
  n.project_id ? `/projects/${n.project_id}/overview` : null

export const NOTIFICATION_CONFIG: Record<string, NotificationTypeConfig> = {
  record_submitted_for_review: {
    icon: 'Send',
    labelFa: 'ارسال برای بررسی',
    message: (n) => `پرونده ${p(n, 'record_display_id')} برای بررسی ارسال شد`,
    route: recordRoute,
  },
  record_approved: {
    icon: 'CheckCircle2',
    labelFa: 'تأیید پرونده',
    message: (n) => `پرونده ${p(n, 'record_display_id')} تأیید شد`,
    route: recordRoute,
  },
  record_declined: {
    icon: 'XCircle',
    labelFa: 'نیازمند اصلاح',
    message: (n) => `پرونده ${p(n, 'record_display_id')} رد شد و نیازمند اصلاح است`,
    route: recordRoute,
  },
  record_resubmitted: {
    icon: 'RotateCcw',
    labelFa: 'ارسال مجدد',
    message: (n) => `پرونده ${p(n, 'record_display_id')} پس از اصلاح دوباره ارسال شد`,
    route: recordRoute,
  },
  record_commented: {
    icon: 'MessageSquare',
    labelFa: 'نظر جدید',
    message: (n) => `نظر جدیدی روی پرونده ${p(n, 'record_display_id')} ثبت شد`,
    route: recordRoute,
  },
  comment_reply: {
    icon: 'Reply',
    labelFa: 'پاسخ به نظر شما',
    message: (n) => `به نظر شما در پرونده ${p(n, 'record_display_id')} پاسخ داده شد`,
    route: recordRoute,
  },
  comment_mention: {
    icon: 'AtSign',
    labelFa: 'اشاره به شما',
    message: (n) => `در نظری روی پرونده ${p(n, 'record_display_id')} به شما اشاره شد`,
    route: recordRoute,
  },
  member_added: {
    icon: 'UserPlus',
    labelFa: 'عضویت در پروژه',
    message: (n) =>
      `به پروژه «${p(n, 'project_name')}» اضافه شدید (نقش: ${roleFa(n, 'role')})`,
    route: overviewRoute,
  },
  member_role_changed: {
    icon: 'UserCog',
    labelFa: 'تغییر نقش',
    message: (n) =>
      `نقش شما در پروژه «${p(n, 'project_name')}» از ${roleFa(n, 'old_role')} به ${roleFa(n, 'new_role')} تغییر کرد`,
    route: overviewRoute,
  },
  form_major_published: {
    icon: 'FileWarning',
    labelFa: 'تغییر مهم فرم',
    message: (n) =>
      `نسخه ${p(n, 'version')} فرم «${p(n, 'form_title')}» با تغییرات مهم منتشر شد`,
    route: recordsListRoute,
  },
  record_marked_for_revision: {
    icon: 'AlertTriangle',
    labelFa: 'نیاز به اصلاح پس از تغییر فرم',
    message: (n) => {
      const count = Number(p(n, 'record_count') || '0')
      const form = p(n, 'form_title')
      if (count === 1) {
        const ids = n.payload?.record_display_ids
        const first = Array.isArray(ids) && typeof ids[0] === 'string' ? ids[0] : ''
        return `پرونده ${first} پس از تغییر فرم «${form}» نیاز به اصلاح دارد`
      }
      return `${count} پرونده پس از تغییر فرم «${form}» نیاز به اصلاح دارند`
    },
    route: (n) => (n.record_id ? recordRoute(n) : recordsListRoute(n)),
  },
}

const FALLBACK: NotificationTypeConfig = {
  icon: 'Bell',
  labelFa: 'اعلان',
  // Legacy / unknown rows: the old producer wrote a prebuilt payload.message;
  // beyond that the DB catalog label (passed by the caller) is the best we have.
  message: (n) => p(n, 'message'),
  route: recordRoute,
}

export function configFor(eventType: string): NotificationTypeConfig {
  return NOTIFICATION_CONFIG[eventType] ?? FALLBACK
}

/**
 * The one-line body for a notification row. `catalogLabelFa` is the DB
 * catalog's label — the dynamic fallback for event types this build of the
 * client does not know about yet.
 */
export function notificationMessage(n: NotificationLike, catalogLabelFa?: string): string {
  const msg = configFor(n.event_type).message(n)
  return msg || catalogLabelFa || configFor(n.event_type).labelFa
}

/** Secondary snippet (comment preview etc.), shown under the message. */
export function notificationSnippet(n: NotificationLike): string {
  return p(n, 'comment_preview')
}
